import { lazy, Suspense } from "react";
import { Navigate } from "react-router-dom";
import useSettings from "hooks/useSettings";
import LoadingScreen from "components/LoadingScreen";
import ProtectedRoute from "components/ProtectedRoute";

import LayoutV3 from "layouts/layout-v3/DashboardLayout";

import Product from "pages/Products/product";
import Products from "pages/Products/product";
import ProductsManagement from "pages/Products/ProductsManagement";
import TalkToAgronomist from "pages/Agronomist/agronomist";
import BulkProducts from "pages/BulkUpload/bulkProduct";
import Farmers from "pages/Farmers/farmers";
import Posts from "pages/Posts";
import Analytics from "pages/Analytics";
import CompanyCategoryManagement from "pages/Products/CompanyCategoryManagement";
import CoursesPage from "pages/Courses";
import EventTypesPage from "pages/EventTypes";
import CropTypesPage from "pages/CropTypes/cropType";
import QuotationProformaInvoice from "pages/QuotationProforma Invoice/QuotationProformaInvoice";
import EnquiryForm from "pages/EnquiryForm";
import FieldVisit from "pages/FieldVisit";
import DiseaseAdvisory from "pages/DiseaseAdvisory/DiseaseAdvisory";
import CropHub from "pages/CropHub";
import AIChatBot from "pages/AIChatBot/AIChatBot";
import Groceries from "pages/Groceries/Groceries";
import Permission from "pages/Permissions/Permission";
import InvoiceDetails from "pages/Invoice/InvoiceDetails";
import Users from "pages/Users/Users";
import ReturnCancelRequest from "pages/ReturnCancelRequest/ReturnCancelRequest";

const Loadable = Component => props => {
  return <Suspense fallback={<LoadingScreen />}>
      <Component {...props} />
    </Suspense>;
}; // dashboards

// Secret Admin Subscription Page

const Login = Loadable(lazy(() => import("./pages/authentication/login")));
const Register = Loadable(lazy(() => import("./pages/authentication/register")));
const NewPassword = Loadable(lazy(() => import("./pages/authentication/new-password")));
const ForgetPassword = Loadable(lazy(() => import("./pages/authentication/forget-password")));
const TwoStepVerify = Loadable(lazy(() => import("./pages/authentication/two-step-verification"))); // admin ecommerce


const Error = Loadable(lazy(() => import("./pages/404")));

const ActiveLayout = () => {
  const {
    settings
  } = useSettings(); // console.log(settings);

  switch (settings.activeLayout) {
    // case "layout1":
    //   return <LayoutV1 />;

    // case "layout2":
    //   return <LayoutV2 />;

    case "layout3":
      return <LayoutV3 />;

    default:
      return <LayoutV3 />;
  }
};

// Authentication guard for login/register pages
const AuthGuard = ({ children }) => {
  const loggedData = JSON.parse(localStorage.getItem("logged_data")) || {};
  const userData = loggedData?.data;
  const isAuthenticated = !!userData;
  const userRole = userData?.user_type;
  const permissions = userData?.permissions || {};

  if (isAuthenticated) {
    // Block Farmer role - clear localStorage and redirect
    if (userRole === 'Farmer') {
      localStorage.clear();
      return <Navigate to="/login" replace />;
    }
    
    // Redirect to appropriate dashboard based on role and permissions
    if (userRole === 'Admin' || permissions.analytics || permissions.dashboard) {
      return <Navigate to="/dashboard/analytics" replace />;
    } else if (permissions.agriStore) {
      return <Navigate to="/dashboard/products-management" replace />;
    } else if (permissions.appOrders) {
      return <Navigate to="/dashboard/orders-management" replace />;
    } else if (permissions.farmerManagement) {
      return <Navigate to="/dashboard/farmers" replace />;
    } else {
      // Find first available page based on permissions
      if (permissions.bulkOrders) return <Navigate to="/dashboard/bulk-upload" replace />;
      if (permissions.quotation) return <Navigate to="/dashboard/Quotation-proforma" replace />;
            if (permissions.enquiry) return <Navigate to="/dashboard/enquiry-form" replace />;
            if (permissions.fieldVisit) return <Navigate to="/dashboard/field-visit" replace />;
            if (permissions.diseaseAdvisory) return <Navigate to="/dashboard/disease-advisory" replace />;
            if (permissions.talkToAgronomist) return <Navigate to="/dashboard/agronomist" replace />;
      if (permissions.eventType) return <Navigate to="/dashboard/event-types" replace />;
      if (permissions.cropType) return <Navigate to="/dashboard/crop-types" replace />;
      if (permissions.farmerForum) return <Navigate to="/dashboard/posts" replace />;
      if (permissions.successStories) return <Navigate to="/dashboard/courses" replace />;
      if (permissions.cropHub) return <Navigate to="/dashboard/crop-hub" replace />;
      // Default fallback
      return <Navigate to="/dashboard/analytics" replace />;
    }
  }

  return children;
};

const routes = () => {
  return [
    {
      path: "/",
      element: (() => {
        const loggedData = JSON.parse(localStorage.getItem("logged_data")) || {};
        const userData = loggedData?.data;
        const userRole = userData?.user_type;
        const permissions = userData?.permissions || {};
        
        if (userData) {
          // Block Farmer role - clear localStorage and redirect
          if (userRole === 'Farmer') {
            localStorage.clear();
            return <Navigate to="/login" replace/>;
          }
          
          // Redirect to appropriate dashboard based on role and permissions
          if (userRole === 'Admin' || permissions.analytics || permissions.dashboard) {
            return <Navigate to="/dashboard/analytics" replace/>;
          } else if (permissions.agriStore) {
            return <Navigate to="/dashboard/products-management" replace/>;
          } else if (permissions.appOrders) {
            return <Navigate to="/dashboard/orders-management" replace/>;
          } else if (permissions.farmerManagement) {
            return <Navigate to="/dashboard/farmers" replace/>;
          } else {
            // Find first available page
            if (permissions.bulkOrders) return <Navigate to="/dashboard/bulk-upload" replace/>;
            if (permissions.quotation) return <Navigate to="/dashboard/Quotation-proforma" replace/>;
            if (permissions.enquiry) return <Navigate to="/dashboard/enquiry-form" replace/>;
            if (permissions.fieldVisit) return <Navigate to="/dashboard/field-visit" replace/>;
            if (permissions.diseaseAdvisory) return <Navigate to="/dashboard/disease-advisory" replace/>;
            if (permissions.talkToAgronomist) return <Navigate to="/dashboard/agronomist" replace/>;
            if (permissions.eventType) return <Navigate to="/dashboard/event-types" replace/>;
            if (permissions.cropType) return <Navigate to="/dashboard/crop-types" replace/>;
            if (permissions.farmerForum) return <Navigate to="/dashboard/posts" replace/>;
            if (permissions.successStories) return <Navigate to="/dashboard/courses" replace/>;
            if (permissions.cropHub) return <Navigate to="/dashboard/crop-hub" replace/>;
            return <Navigate to="/dashboard/analytics" replace/>;
          }
        }
        return <Navigate to="/login" replace/>;
      })()
    },
    {
      path: "login",
      element: <AuthGuard><Login /></AuthGuard>,
      title: "Login"
    },
    {
      path: "register",
      element: <AuthGuard><Register /></AuthGuard>,
      title: "Register"
    },
    
    {
      path: "dashboard",
      element: (
        <ProtectedRoute>
          <ActiveLayout />
        </ProtectedRoute>
      ),
      children: [
        {
          path: "",
          element: (
            <ProtectedRoute requiredPermission="analytics">
              <Analytics />
            </ProtectedRoute>
          ),
          title: "Analytics"
        },
        {
          path: "analytics",
          element: (
            <ProtectedRoute requiredPermission="analytics">
              <Analytics />
            </ProtectedRoute>
          ),
          title: "Analytics"
        },
        {
          path: "orders-management",
          element: (
            <ProtectedRoute requiredPermission="appOrders">
              <Products />
            </ProtectedRoute>
          ),
          title: "Orders Management"
        },
        {
          path: "products",
          element: (
            <ProtectedRoute requiredPermission="agriStore">
              <Product />
            </ProtectedRoute>
          ),
          title: "Products"
        },
        {
          path: "agronomist",
          element: (
            <ProtectedRoute requiredPermission="talkToAgronomist">
              <TalkToAgronomist />
            </ProtectedRoute>
          ),
          title: "Agronomist"
        },
        {
          path: "bulk-upload",
          element: (
            <ProtectedRoute requiredPermission="bulkOrders">
              <BulkProducts />
            </ProtectedRoute>
          ),
          title: "Bulk Upload"
        },
        {
          path: "courses",
          element: (
            <ProtectedRoute requiredPermission="successStories">
              <CoursesPage />
            </ProtectedRoute>
          ),
          title: "Courses"
        },
        {
          path: "farmers",
          element: (
            <ProtectedRoute requiredPermission="farmerManagement">
              <Farmers />
            </ProtectedRoute>
          ),
          title: "Farmers"
        },
        {
          path: "products-management",
          element: (
            <ProtectedRoute requiredPermission="agriStore">
              <ProductsManagement />
            </ProtectedRoute>
          ),
          title: "Products Management"
        },
        {
          path: "company-category-management",
          element: (
            <ProtectedRoute allowedRoles={["Admin", "Company"]}>
              <CompanyCategoryManagement />
            </ProtectedRoute>
          ),
          title: "Company & Category Management"
        },
        {
          path: "posts",
          element: (
            <ProtectedRoute requiredPermission="farmerForum">
              <Posts />
            </ProtectedRoute>
          ),
          title: "Posts"
        },
        {
          path: "event-types",
          element: (
            <ProtectedRoute requiredPermission="eventType">
              <EventTypesPage />
            </ProtectedRoute>
          ),
          title: "Event Types"
        },
        {
          path: "crop-types",
          element: (
            <ProtectedRoute requiredPermission="cropType">
              <CropTypesPage />
            </ProtectedRoute>
          ),
          title: "Crop Types"
        },
        {
          path: "Quotation-proforma",
          element: (
            <ProtectedRoute requiredPermission="quotation">
              <QuotationProformaInvoice />
            </ProtectedRoute>
          ),
          title: "Quotation-proforma"
        },
        {
          path: "enquiry-form",
          element: (
            <ProtectedRoute requiredPermission="enquiry">
              <EnquiryForm />
            </ProtectedRoute>
          ),
          title: "Enquiry Form"
        },
        {
          path: "field-visit",
          element: (
            <ProtectedRoute requiredPermission="fieldVisit">
              <FieldVisit />
            </ProtectedRoute>
          ),
          title: "Field Visit"
        },
        {
          path: "disease-advisory",
          element: (
            <ProtectedRoute requiredPermission="diseaseAdvisory">
              <DiseaseAdvisory />
            </ProtectedRoute>
          ),
          title: "Disease Advisory"
        },
          {
            path: "crop-hub",
            element: (
              <ProtectedRoute requiredPermission="cropHub">
                <CropHub />
              </ProtectedRoute>
            ),
            title: "Crop Hub"
          },
          {
            path: "groceries",
            element: (
              <ProtectedRoute requiredPermission="groceries">
                <Groceries />
              </ProtectedRoute>
            ),
            title: "Groceries"
          },
        {
          path: "ai-chat-bot",
          element: (
            <ProtectedRoute requiredPermission="aiChatBot">
              <AIChatBot />
            </ProtectedRoute>
          ),
          title: "AI Chat Bot"
        },
          {
          path: "permissions",
          element: (
            <ProtectedRoute requiredPermission="permission">
              <Permission />
            </ProtectedRoute>
          ),
          title: "Permission"
        },
         {
          path: "invoice",
          element: (
            <ProtectedRoute requiredPermission="invoice">
              <InvoiceDetails />
            </ProtectedRoute>
          ),
          title: "Invoice"
        },
         {
          path: "users",
          element: (
            <ProtectedRoute requiredPermission="invoice">
              <Users />
            </ProtectedRoute>
          ),
          title: "Users"
        },
        {
          path: "return-cancel-request",
          element: (
            <ProtectedRoute requiredPermission="returnCancelRequest">
              <ReturnCancelRequest />
            </ProtectedRoute>
          ),
          title: "Return & Cancel Request"
        },
      ]
    },
    {
      path: "*",
      element: <Error />,
      title: "404 Error"
    }
  ];
};

export default routes;